<template>
  <div v-if="data" class="flex flex-col gap-3">
    <div v-for="(day, i) in carsPerDay" :key="i" class="flex flex-col gap-2">
      <div class="text-sm text-primary">
        <span class="font-medium">{{ formatDate(day.day, 'cccc') }}</span>
        —
        {{ formatDate(day.day, getDateFormat()) }}
      </div>
      <div class="flex flex-col gap-2">
        <div v-for="car in day.cars" :key="car.id">
          <DetailingListItem
            :car="car"
            :call-label="props.mode === 'detailing' ? t('detail') : t('fleetIn')"
          />
        </div>
      </div>
    </div>
  </div>

  <div v-if="status === 'success' && data?.pages.length === 0" class="flex justify-center py-28">
    <p>{{ t('noCarsFound') }}</p>
  </div>

  <div v-if="status === 'pending'" class="flex justify-center py-28">
    <Spinner />
  </div>

  <div v-if="status === 'error'" class="flex justify-center py-28">
    <p>{{ error }}</p>
  </div>

  <div v-if="hasNextPage" class="flex justify-center pt-8">
    <CVButton outline :is-loading="isFetchingNextPage" @click="() => fetchNextPage()">
      {{ t('loadMore') }}
    </CVButton>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { useCarsWithQuery } from '@/queries/use-cars';
import DetailingListItem from './DetailingListItem.vue';
import { useCurrentStations } from '@/hooks/use-current-stations';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { formatDate } from '@/hooks/use-formatted-date';
import { getDateFormat } from '@/hooks/use-date-format';
import { FindAllCarsSort } from '@/entities/cars/find-all-cars-sort.enum';
import { groupByDateTimeUnit } from '@/utils/group-by-date-time-unit';
import { CarStatus } from '@/entities/cars/car-status.enum';
import { Order } from '@/entities/pagination/order.enum';

const props = defineProps<{
  mode: 'detailing' | 'fleetIn';
}>();

const { t } = useI18n();

const currentStations = useCurrentStations();

const getCarsQuery = computed(() => ({
  stationIds: currentStations.value,
  sort: [FindAllCarsSort.NEXT_ALLOCATION_START_DATE],
  order: Order.ASC,
  includeNextAllocationStartDate: true,
  status: props.mode === 'fleetIn' ? [CarStatus.ORDERED] : [CarStatus.FLOAT_IN],
  readyToHandover: props.mode === 'detailing' ? false : undefined,
  hasNextAllocationStartDate: props.mode === 'fleetIn' ? true : undefined,
  isSetregistrationAndOrganizationDoneDate: props.mode === 'fleetIn' ? true : undefined,
}));

const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, error } =
  useCarsWithQuery(getCarsQuery);

const cars = useFlattenPaginatedData(data);

const carsPerDay = computed(() =>
  groupByDateTimeUnit(cars.value, (car) => car.nextAllocationStartDate ?? '', 'day', 'cars'),
);
</script>

<i18n lang="json">
{
  "en": {
    "handovers": "Handovers",
    "upcomingHandovers": "Upcoming handovers",
    "all": "All",
    "pickups": "Pickups",
    "returns": "Returns",
    "detailing": "Detailing",
    "noCarsFound": "No cars found that need to be detailed",
    "tomorrow": "Tomorrow",
    "today": "Today",
    "detail": "Detail until",
    "fleetIn": "Fleet in until"
  },
  "de": {
    "handovers": "Übergaben / Rückgaben",
    "upcomingHandovers": "Übergaben & Rückgaben der nächsten 24h",
    "all": "Alles",
    "pickups": "Übergaben",
    "returns": "Rückgaben",
    "detailing": "Aufbereiten",
    "noCarsFound": "Keine Fahrzeuge gefunden, die aufbereitet werden müssen",
    "tomorrow": "Morgen",
    "today": "Heute",
    "detail": "Aufbereiten bis",
    "fleetIn": "Einflotten bis"
  },
  "es": {
    "handovers": "Entregas / devoluciones",
    "upcomingHandovers": "Próximas entregas y devoluciones",
    "all": "Todo",
    "pickups": "Entregas",
    "returns": "Devoluciones",
    "detailing": "Detailing",
    "noCarsFound": "No hay vehículos pendientes de detailing",
    "tomorrow": "Mañana",
    "today": "Hoy",
    "detail": "Detailing hasta",
    "fleetIn": "Entrada a flota hasta"
  }
}
</i18n>
